import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { truncateText } from "@/components/equipment/equipmentLabels";
import { dateFnsLocaleFor } from "@/lib/appLocale";

export interface EquipmentGearRow {
  id: string;
  type: string;
  name: string;
  serialNumber?: string | null;
  condition?: string | null;
  status: string;
  date?: string | null;
  canReturn: boolean;
  onReturn: () => void;
}

export function EquipmentGearTable({
  title,
  empty,
  rows,
}: {
  title: string;
  empty: string;
  rows: EquipmentGearRow[];
}) {
  const { t, i18n } = useTranslation();
  const dateLocale = dateFnsLocaleFor(i18n.language);

  return (
    <div>
      <h3 className="mb-2 text-sm font-medium">{title}</h3>
      {rows.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">{empty}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("admin.equipment.fields.type")}</TableHead>
              <TableHead>{t("admin.equipment.fields.name")}</TableHead>
              <TableHead>{t("admin.equipment.fields.serial")}</TableHead>
              <TableHead>{t("admin.equipment.fields.condition")}</TableHead>
              <TableHead>{t("admin.equipment.fields.status")}</TableHead>
              <TableHead>{t("admin.equipment.fields.date")}</TableHead>
              <TableHead>{t("admin.equipment.fields.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Badge variant="secondary">{row.type}</Badge>
                </TableCell>
                <TableCell className="font-medium">{row.name}</TableCell>
                <TableCell>{row.serialNumber || "—"}</TableCell>
                <TableCell className="max-w-[200px] text-sm text-muted-foreground">
                  {truncateText(row.condition, 80)}
                </TableCell>
                <TableCell>{row.status}</TableCell>
                <TableCell>
                  {row.date ? format(new Date(row.date), "dd.MM.yyyy", { locale: dateLocale }) : "—"}
                </TableCell>
                <TableCell>
                  {row.canReturn && (
                    <Button size="sm" variant="outline" onClick={row.onReturn}>
                      {t("admin.equipment.gear.return")}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
